import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import {
  companyResolutionKey,
  resolveCompany,
} from "@/lib/company-resolution";
import { companyLinkOpeningStore } from "@/stores/company-link-opening-store";
import { companyPreviewPanelStore } from "@/stores/company-preview-panel-store";
import type { MouseEvent } from "react";

/**
 * On a phone a company is read in the bottom sheet, never on a page of its own:
 * a link to `/company/:id` opens the preview panel there instead of
 * navigating. Returns the link's `onClick`; on a desktop it does nothing, and
 * the link behaves as before. A caller with no click to cancel (Enter on a
 * highlighted row) passes `null` for the event.
 */
export function useCompanyLinkInPanel(): (
  event: Pick<MouseEvent, "preventDefault"> | null,
  company: { readonly id: string; readonly label: string }
) => void {
  const api = useApi();
  const toastError = useToastError();
  const { isMobile } = useDeviceFormat();

  return (event, { id, label }) => {
    if (!isMobile) return;
    // Always set for an id; typed nullable for the identifier-only lookups.
    const key = companyResolutionKey({ id });
    if (!key) return;
    event?.preventDefault();
    // The panel shows the company by name at once, while it is read: the
    // panel only takes a company in hand, and a tap must never look dead.
    const tap = companyLinkOpeningStore.start(label);
    const tappedOn = window.location.pathname;
    // Dropped when the user tapped another company since, closed the panel,
    // or left the screen the link was on: it must not open over what is now
    // on show — and a slower result for an earlier tap never replaces it.
    const isStillWanted = () =>
      companyLinkOpeningStore.isCurrent(tap) &&
      window.location.pathname === tappedOn;
    resolveCompany(api, key, { id })
      .then((resolution) => {
        const isWanted = isStillWanted();
        // The panel takes the company before the opening state goes, so the
        // sheet stays up from one to the other.
        if (isWanted && resolution.status === "company") {
          // As a company mention in the same conversation opens it: the
          // conversation stays live above it, not under a modal scrim.
          companyPreviewPanelStore.open(
            resolution.company,
            [resolution.company],
            { modal: false },
          );
        }
        // A superseded tap's opening state is the next tap's: left alone.
        companyLinkOpeningStore.finish(tap);
        if (isWanted && resolution.status !== "company") toastError();
      })
      .catch(() => {
        const isWanted = isStillWanted();
        companyLinkOpeningStore.finish(tap);
        if (isWanted) toastError();
      });
  };
}
